import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';
import { callAdmin, supabase } from '@/integrations/supabase/client';

// ── Types ─────────────────────────────────────────────────────────────────────
// education / sponsorship / whitelabel / revenue are optional: the page renders
// with '—' placeholders until the admin function's get_dashboard_stats action
// returns them (extension spec in the session notes). Everything degrades
// gracefully — no breakage on the current backend shape.

interface DashboardStats {
  users: {
    total: number; free: number; premium: number; cancelled: number; recent: number;
    paidSeats?: number;
  };
  conversations: { total: number; today: number; recent: number; voice?: number; voiceToday?: number };
  messages: { total: number; voice?: number; text?: number };
  support?: {
    openTickets: number;
    escalatedTickets: number;
  };
  demographics: {
    ageBrackets: Record<string, number>;
    topCountries: Array<{ country: string; count: number }>;
    signupTrends: Array<{ date: string; signups: number }>;
  };
  education?: {
    activeLearnersToday: number;
    lessonsCompleted7d: number;
    quizzesToday: number;
    quizzes7d: number;
    quizPassRate7d: number;   // 0-100
    certificatesIssued: number;
  };
  sponsorship?: {
    poolSeatsAvailable: number;
    hardshipAwaiting: number;
    orgChildrenPending: number;
    ngoRequestsPending: number;
    activeSponsorships: number;
    sponsoredSeats: number;
  };
  whitelabel?: {
    orgsLive: number;
    seatsAllocated: number;
    seatsUsed: number;
  };
  revenue?: {
    mrrEstimate: number;
    paidSeats?: number;
    planBreakdown: Array<{ plan: string; users: number; priceMonthly: number }>;
  };
}

const num = (v: number | undefined) => (typeof v === 'number' ? v.toLocaleString() : '—');

// ── Small building blocks ─────────────────────────────────────────────────────

function HeroStat({ icon, label, value, accent }: { icon: string; label: string; value: string; accent: string }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${accent}`}>{icon}</div>
        <div className="min-w-0">
          <p className="text-2xl font-black text-foreground leading-tight">{value}</p>
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mt-0.5">{label}</p>
        </div>
      </div>
    </Card>
  );
}

function QueueItem({ icon, label, value, actionLabel, onClick, tone }:
  { icon: string; label: string; value: string; actionLabel?: string; onClick?: () => void; tone: 'red' | 'amber' | 'green' | 'blue' }) {
  const tones = {
    red: 'bg-red-500/10 border-red-500/20 text-red-600 dark:text-red-400',
    amber: 'bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400',
    green: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400',
  };
  return (
    <div className={`flex items-center justify-between p-4 rounded-xl border ${tones[tone]}`}>
      <div className="flex items-center gap-3">
        <span className="text-lg">{icon}</span>
        <div>
          <p className="text-sm font-bold">{label}</p>
          <p className="text-xl font-black leading-tight">{value}</p>
        </div>
      </div>
      {actionLabel && onClick && (
        <button onClick={onClick} className="px-4 py-2 rounded-lg bg-card border border-current text-sm font-bold hover:bg-muted">
          {actionLabel} →
        </button>
      )}
    </div>
  );
}

function SectionCard({ title, children, sub }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <Card className="p-6">
      <h3 className="text-base font-bold text-foreground mb-1">{title}</h3>
      {sub && <p className="text-xs text-muted-foreground mb-4">{sub}</p>}
      {children}
    </Card>
  );
}

function StatLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-bold text-foreground">{value}</span>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export const AdminStats: React.FC<{
  onNavigateTab?: (tab: string) => void;
  ticketStats?: { open: number; escalated: number };
}> = ({ onNavigateTab, ticketStats: propTicketStats }) => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localTicketStats, setLocalTicketStats] = useState<{ open: number; escalated: number } | null>(null);

  useEffect(() => {
    fetchDashboardStats();
    if (!propTicketStats) {
      supabase.functions.invoke('support', { body: { action: 'admin_list_threads' } })
        .then(({ data }) => {
          if (data?.threads) {
            const threads = data.threads as Array<{ status: string; escalated: boolean }>;
            const open = threads.filter(t => t.status === 'open' || (t.escalated && t.status !== 'resolved'));
            const escalated = threads.filter(t => t.escalated && t.status !== 'resolved');
            setLocalTicketStats({ open: open.length, escalated: escalated.length });
          }
        })
        .catch(() => {});
    }
  }, [propTicketStats]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const result = await callAdmin<DashboardStats>('get_dashboard_stats');
      if (result.success) {
        setStats(result.data as DashboardStats);
      } else {
        setError(result.error || 'Failed to fetch dashboard stats');
      }
    } catch (err) {
      setError('Failed to fetch dashboard stats');
      console.error('Error fetching dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-md p-4 text-red-600 dark:text-red-400">
          <p className="font-bold mb-2">⚠️ {error}</p>
          <Button onClick={fetchDashboardStats} className="bg-red-600 text-white">Retry</Button>
        </div>
      </div>
    );
  }

  if (!stats) return <div className="p-8 text-center text-muted-foreground">No data available</div>;

  const edu = stats.education;
  const spon = stats.sponsorship;
  const wl = stats.whitelabel;
  const rev = stats.revenue;

  const today = new Date().toISOString().split('T')[0];
  const signupsToday = (stats.demographics?.signupTrends || []).reduce((s, t) => (t.date === today ? s + t.signups : s), 0);
  const signupsWeek = (stats.demographics?.signupTrends || []).reduce((s, t) => s + t.signups, 0);

  const effectiveTicketStats = propTicketStats || localTicketStats || (stats?.support ? { open: stats.support.openTickets, escalated: stats.support.escalatedTickets } : { open: 0, escalated: 0 });
  const openCount = effectiveTicketStats.open ?? 0;
  const isEscalated = (effectiveTicketStats.escalated ?? 0) > 0;

  const queueHasWork = (spon && (spon.hardshipAwaiting > 0 || spon.orgChildrenPending > 0 || spon.ngoRequestsPending > 0)) || openCount > 0;

  return (
    <div className="p-8 space-y-8">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-1">Command Center</h2>
          <p className="text-muted-foreground">The whole platform in one screen — learning, money, and what needs you</p>
        </div>
        <Button onClick={fetchDashboardStats} className="bg-blue-600 text-white">
          🔄 Refresh
        </Button>
      </div>

      {/* ── TODAY ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <HeroStat icon="🔥" label="Learners active today" value={num(edu?.activeLearnersToday)} accent="bg-orange-100" />
        <HeroStat icon="🧠" label="Quizzes today" value={num(edu?.quizzesToday)} accent="bg-indigo-100" />
        <HeroStat icon="✨" label="New signups today" value={String(signupsToday)} accent="bg-emerald-100" />
        <HeroStat icon="🎓" label="Certificates issued" value={num(edu?.certificatesIssued)} accent="bg-amber-100" />
        <HeroStat icon="💳" label="Paid seats" value={num(rev?.paidSeats ?? stats.users?.premium)} accent="bg-blue-100" />
      </div>

      {/* ── THE OPS QUEUE — what needs you right now ── */}
      <SectionCard title="Needs you today" sub="The operational queue — every number here is a person waiting on an action">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          <QueueItem
            icon="💬"
            label={isEscalated ? "Support tickets (🆘 Human requested)" : "Support tickets awaiting reply"}
            value={num(openCount)}
            actionLabel="Reply"
            tone={isEscalated ? "red" : openCount > 0 ? "amber" : "green"}
            onClick={onNavigateTab ? () => onNavigateTab('support') : undefined}
          />
          <QueueItem icon="🤝" label="Hardship applicants awaiting a seat" value={num(spon?.hardshipAwaiting)}
            actionLabel="Assign" tone="red"
            onClick={onNavigateTab ? () => onNavigateTab('sponsorships') : undefined} />
          <QueueItem icon="🏫" label="Org children pending assignment" value={num(spon?.orgChildrenPending)}
            actionLabel="Assign" tone="amber"
            onClick={onNavigateTab ? () => onNavigateTab('sponsorships') : undefined} />
          <QueueItem icon="🌍" label="NGO seat requests to review" value={num(spon?.ngoRequestsPending)}
            actionLabel="Review" tone="amber"
            onClick={onNavigateTab ? () => onNavigateTab('sponsorships') : undefined} />
          <QueueItem icon="🌱" label="Pay-it-forward pool seats available" value={num(spon?.poolSeatsAvailable)} tone="green" />
          <QueueItem icon="💚" label="Active sponsorships / funded seats" value={`${num(spon?.activeSponsorships)} / ${num(spon?.sponsoredSeats)}`} tone="green" />
          <QueueItem icon="🏢" label="White-label portals live" value={num(wl?.orgsLive)}
            actionLabel="Facilities" tone="blue"
            onClick={onNavigateTab ? () => onNavigateTab('facilities') : undefined} />
        </div>
        {queueHasWork === false && (
          <p className="text-sm text-emerald-600 font-bold mt-3">✅ Queue clear — nothing waiting on you.</p>
        )}
      </SectionCard>

      {/* ── THE ENGINE + THE MONEY ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="The engine — last 7 days" sub="Learning activity and outcomes">
          <StatLine label="Lessons completed" value={num(edu?.lessonsCompleted7d)} />
          <StatLine label="Quizzes taken" value={num(edu?.quizzes7d)} />
          <StatLine label="Quiz pass rate (≥70%)" value={typeof edu?.quizPassRate7d === 'number' ? `${edu.quizPassRate7d}%` : '—'} />
          <StatLine label="Conversations today (companion)" value={num(stats.conversations?.today)} />
          <StatLine label="Voice conversations today" value={num(stats.conversations?.voiceToday)} />
        </SectionCard>

        <SectionCard title="The money" sub="List anchors the deals — this is the reality">
          <StatLine label="MRR estimate" value={typeof rev?.mrrEstimate === 'number' ? `$${rev.mrrEstimate.toLocaleString()}` : '—'} />
          <StatLine label="Premium users" value={num(stats.users?.premium)} />
          <StatLine label="White-label seats used / allocated" value={`${num(wl?.seatsUsed)} / ${num(wl?.seatsAllocated)}`} />
          {(rev?.planBreakdown || []).slice(0, 5).map((p) => (
            <StatLine key={p.plan} label={p.plan} value={`${p.users} × $${p.priceMonthly}`} />
          ))}
        </SectionCard>
      </div>

      {/* ── GROWTH ── */}
      <SectionCard title="Growth — last 7 days" sub={`${signupsWeek} signups this week`}>
        <div className="grid grid-cols-7 gap-2">
          {(stats.demographics?.signupTrends || []).map((trend, i) => (
            <div key={i} className="text-center">
              <div className="text-xs text-muted-foreground mb-1">
                {new Date(trend.date).toLocaleDateString('en-US', { weekday: 'short' })}
              </div>
              <div className="text-sm font-bold text-foreground">{trend.signups}</div>
              <div className="text-[10px] text-muted-foreground">
                {new Date(trend.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* ── AUDIENCE + COMPANION (compact) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Audience">
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.demographics?.ageBrackets || {}).map(([bracket, count]) => (
              <span key={bracket} className="px-3 py-1.5 rounded-lg bg-muted border border-border text-sm font-bold text-foreground">
                {bracket.replace(/_/g, ' ')}: {count}
              </span>
            ))}
            {(stats.demographics?.topCountries || []).slice(0, 3).map((c) => (
              <span key={c.country} className="px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm font-bold text-blue-600 dark:text-blue-400">
                {c.country}: {c.count}
              </span>
            ))}
          </div>
        </SectionCard>
        <SectionCard title="Companion surface">
          <StatLine label="Total conversations" value={num(stats.conversations?.total)} />
          <StatLine label="Total messages" value={num(stats.messages?.total)} />
          <StatLine label="Voice users" value={num(stats.conversations?.voice)} />
        </SectionCard>
      </div>
    </div>
  );
};